import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { configSelector } from 'store/config/selectors';
import { getCurrentUser } from 'store/user/actions';
import { isServerUnreachable } from 'store/user/network-failure';
import { userState } from 'store/user/selectors';

/** First automatic retry, doubled after each failed one up to the cap. */
export const RETRY_BASE_DELAY_MS = 2000;
export const RETRY_MAX_DELAY_MS = 30000;

interface UserConnectionFailure {
  /** Booting with no user, because no app server answered the user fetch. Held through a retry. */
  isConnectionLost: boolean;
  retry: VoidFunction;
}

export const useUserConnectionFailure = (): UserConnectionFailure => {
  const dispatch = useDispatch();
  const userSlice = useSelector(userState);
  const { authToken, appBrand, buildingUuid } = useSelector(configSelector);
  const hasLostConnectionRef = useRef(false);
  const failedRetriesRef = useRef(0);

  const hasUser = Boolean(userSlice?.user);
  const isLoading = Boolean(userSlice?.loading);
  const isUnreachable = isServerUnreachable(userSlice?.error);

  // The reducer keeps a user it already had when the server is unreachable, so this is cold-boot only.
  if (hasUser || (!isUnreachable && !isLoading)) {
    hasLostConnectionRef.current = false;
    failedRetriesRef.current = 0;
  } else if (isUnreachable) {
    hasLostConnectionRef.current = true;
  }

  // `request` clears the error; held while the retry is in flight so the screen does not blink.
  const isConnectionLost = !hasUser && (isUnreachable || (isLoading && hasLostConnectionRef.current));

  // From config, not the URL the boot read: the SDK writes refreshed tokens there. Never gated on
  // `loading`: a failure the middleware still swallows would leave it true and the button dead, and the
  // epic's `switchMap` already makes a second press safe.
  const retry = useCallback(() => {
    dispatch(getCurrentUser.request({ authToken, appBrand, buildingUuid }));
  }, [dispatch, authToken, appBrand, buildingUuid]);

  // The automatic triggers fire together on foreground, so they skip while a request is in flight.
  const autoRetry = useCallback(() => {
    if (!isLoading) {
      retry();
    }
  }, [isLoading, retry]);

  const isWaiting = isConnectionLost && !isLoading;

  // A webview left open while the network quietly returns fires none of the events below.
  useEffect(() => {
    if (!isWaiting) {
      return undefined;
    }
    const delay = Math.min(RETRY_MAX_DELAY_MS, RETRY_BASE_DELAY_MS * 2 ** failedRetriesRef.current);
    const timer = setTimeout(() => {
      failedRetriesRef.current += 1;
      autoRetry();
    }, delay);

    return () => clearTimeout(timer);
  }, [isWaiting, autoRetry]);

  // A dead-network boot loses focus within milliseconds, so coming back into view is the cue.
  useEffect(() => {
    if (!isWaiting) {
      return undefined;
    }
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        autoRetry();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', autoRetry);
    window.addEventListener('online', autoRetry);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', autoRetry);
      window.removeEventListener('online', autoRetry);
    };
  }, [isWaiting, autoRetry]);

  return { isConnectionLost, retry };
};
